
import {
  Component,
  OnDestroy,
  OnInit,
  forwardRef,
  input
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { Subject, takeUntil } from 'rxjs';

export interface SelectOption {
  value: string;
  label: string;
}

function normalizeOptions(value: (string | SelectOption)[]): SelectOption[] {
  if (!value) return [];
  return value.map((item) =>
    typeof item === 'string' ? { value: item, label: item } : item,
  );
}

@Component({
  selector: 'app-searchable-multi-select',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule
],
  templateUrl: './searchable-multi-select.component.html',
  styleUrls: ['./searchable-multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableMultiSelectComponent),
      multi: true,
    },
  ],
})
export class SearchableMultiSelectComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  /** Label shown on the form field */
  readonly label = input('Select');

  /** Placeholder for the search input */
  readonly searchPlaceholder = input('Search...');

  /**
   * When set, the field always shows this text in its trigger instead of the
   * selected values (which are expected to be displayed elsewhere, e.g. as
   * chips). Also used as the empty-state placeholder.
   */
  readonly triggerText = input('');

  /**
   * Options to display. Accepts either:
   * - string[] — value and label are the same
   * - SelectOption[] — {value, label} objects
   */
  readonly options = input<SelectOption[], (string | SelectOption)[]>([], {
    transform: normalizeOptions,
  });

  /**
   * Message shown when the field has no options at all — a different situation
   * from a search that matched nothing, which would make "No results found"
   * misleading (it implies the user's search is at fault). Callers should pass
   * something specific, e.g. "No skills have been added".
   */
  readonly emptyOptionsText = input('No options available');

  /** Panel CSS class for the mat-select overlay */
  readonly panelClass = input('searchable-select-panel');

  /** When true, applies red-border error styling to the form field */
  readonly hasError = input(false);

  /**
   * Option values that should be highlighted as needing selection
   * (e.g. practice area codes belonging to requestors with no coverage).
   */
  readonly highlightedValues = input<string[]>([]);

  selectControl = new FormControl<string[]>([]);
  searchTerm = '';

  private destroy$ = new Subject<void>();
  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.selectControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.onChange(value ?? []);
        this.onTouched();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ControlValueAccessor
  writeValue(value: string[]): void {
    this.selectControl.setValue(value ?? [], { emitEvent: false });
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled
      ? this.selectControl.disable({ emitEvent: false })
      : this.selectControl.enable({ emitEvent: false });
  }

  /**
   * Visible options ordered as:
   *   1. Currently selected
   *   2. Highlighted (e.g. uncovered practice areas) but not yet selected
   *   3. Everything else
   * All groups are filtered by the active search term.
   */
  getFilteredOptions(): SelectOption[] {
    const search = this.searchTerm.trim().toLowerCase();
    const selected = new Set(this.selectControl.value ?? []);
    const highlighted = new Set(this.highlightedValues());

    const matching = search
      ? this.options().filter((o) => o.label.toLowerCase().includes(search))
      : this.options();

    return [
      ...matching.filter((o) => selected.has(o.value)),
      ...matching.filter((o) => !selected.has(o.value) && highlighted.has(o.value)),
      ...matching.filter((o) => !selected.has(o.value) && !highlighted.has(o.value)),
    ];
  }

  /**
   * Selected options that are NOT in the current visible list. These are rendered
   * as hidden mat-options so mat-select keeps them in its value while the user
   * filters; otherwise mat-select would drop values whose options aren't in the DOM.
   */
  getHiddenSelectedOptions(): SelectOption[] {
    const visibleValues = new Set(this.getFilteredOptions().map((o) => o.value));
    const selected = new Set(this.selectControl.value ?? []);
    return this.options().filter(
      (o) => selected.has(o.value) && !visibleValues.has(o.value),
    );
  }

  /**
   * Empty-state message for the panel, or null when options are showing.
   * Separates "nothing to choose from" from "your search matched nothing" so the
   * panel never claims a search failed when the list was empty to begin with.
   */
  getEmptyStateText(): string | null {
    if (this.getFilteredOptions().length > 0) {
      return null;
    }
    return this.options().length === 0
      ? this.emptyOptionsText()
      : 'No results found';
  }

  /** Get display text showing count of selected items */
  getSelectedCountText(): string {
    const count = (this.selectControl.value ?? []).length;
    return count > 0 ? `(${count})` : '';
  }

  /** Clear search term when panel closes */
  onPanelToggle(opened: boolean): void {
    if (!opened) {
      this.searchTerm = '';
    }
  }
}
